// Feed.tsx — Phase 11 Step 3 Block 5
// Home-Feed-Komposition für portal.scrolly.com.
//
// Struktur (Briefing):
//   1. LoggedOutHero (Greeting)
//   2. CategoryChips (sticky, interaktiv, aber kein Routing)
//   3. FeaturedHero (erstes featured Scrolly)
//   4. Row "Im Trend"                   — 4 Cards, chronologisch (Briefing)
//   5. Row "Empfehlungen der Redaktion" — 4 Cards, featured-first (Briefing)
//   6. Row mit Kategorie-Label          — Wissenschaft, alle verfügbaren (s.u.)
//
// Quelle-Abgleich (Scrolly Platform.html:919–1005 `HomeFeed`):
//   Die Quelle hat mehr Rows (Trending, Creators, Because-you-read, Editor's picks,
//   CategorySpotlight, "Fresh scrollys this week"). Für Step 3 folgen wir dem
//   Briefing mit drei Rows — die Quelle validiert nur das Layout (Grid/Gap/Titel).
//
// Row-3-Abweichung vom Briefing-Wortlaut:
//   Briefing: "dritte Reihe mit Kategorie-Label (z.B. Wissenschaft), 4 featured-first".
//   Unser Mock-Set hat nur 3 Science-Mocks (s9, s3, s14). Keine andere
//   Kategorie hat 4 Items. Wir rendern daher **3** Wissenschafts-Scrollys
//   featured-first — die CardGrid-4-Spalten bleibt, die letzte Spalte leer.
//   Das ist bewusste Transparenz gegenüber dem Mock-Datensatz; bei echten
//   Daten (Step 5+) wird es automatisch voll.
//
// `isLoading`-Dev-Toggle:
//   `const [isLoading, setIsLoading] = useState(false)`. Um die Skeleton-
//   Variante im Browser zu sehen: den Initial-Wert auf `true` flippen, dev
//   reloaden. In Step 5 wird das an Query-State gekoppelt.
//
// Routing (Phase 11 Step 4 Block 2):
//   - Cards + CTAs verlinken auf `/s/:slug`, Creator-Chips auf `/@:handle`,
//     Category-Chips auf `/` bzw. `/category/:id`.
//   - Feed bleibt kurativ (keine Client-Filterung) — die `/category/:id`-
//     Route hat ihre eigene Page, die `ALL_SCROLLYS.filter(...)` rendert.
//   - CategoryChips-Activestate kommt aus `pageContext.urlPathname`;
//     dieser Feed muss keinen Controlled-State mehr halten.

import { useMemo, useState } from "react";
import { CATEGORIES, type CategoryId } from "../../lib/categories";
import { VARIANT } from "../../lib/tokens";
import { useVariant } from "../../hooks/useVariant";
import { scrollys as ALL_SCROLLYS } from "../../lib/mock/scrollys";
import { CategoryChips } from "./CategoryChips";
import { SectionTitle } from "./SectionTitle";
import { CardGrid } from "./CardGrid";
import { ScrollyCard } from "./ScrollyCard";
import { FeaturedHero } from "./FeaturedHero";
import { LoggedOutHero } from "../home/LoggedOutHero";
import { FeedSkeleton } from "./FeedSkeleton";

// ---- Row-Kategorie für Reihe 3 -----------------------------------------

const SPOTLIGHT_CATEGORY_ID: CategoryId = "science";

function spotlightLabel(): string {
  const cat = CATEGORIES.find((c) => c.id === SPOTLIGHT_CATEGORY_ID);
  return cat?.label ?? "Spotlight";
}

// ---- Komponente --------------------------------------------------------

export function Feed() {
  const { variant } = useVariant();
  const v = VARIANT[variant];

  // Dev-Toggle. Initial false — auf `true` flippen zum visuellen Check der Skeleton.
  const [isLoading] = useState(false);

  // Featured-Scrolly für Hero: erstes featured:true.
  const featured = useMemo(
    () => ALL_SCROLLYS.find((s) => s.featured) ?? ALL_SCROLLYS[0],
    [],
  );

  // Row 1 — "Im Trend": 4 Cards chronologisch.
  // Chronologisch = nach Position im Mock-Array (das ist unsere Proxy für
  // "neueste zuerst", weil `publishedAt` ein Freitext-Label ist).
  // Featured wird ausgeschlossen, damit Hero + Row 1 nicht kollidieren.
  const trendingRow = useMemo(() => {
    return ALL_SCROLLYS.filter((s) => s.id !== featured.id).slice(0, 4);
  }, [featured.id]);

  // Row 2 — "Empfehlungen der Redaktion": 4 Cards, featured-first, dann Rest.
  // Auch hier Hero-Scrolly ausgeschlossen, sonst taucht s1 doppelt auf.
  // Dedup gegen Row 1, damit eine Card nicht in beiden Rows erscheint.
  const editorsRow = useMemo(() => {
    const featuredPool = ALL_SCROLLYS.filter(
      (s) => s.featured && s.id !== featured.id,
    );
    const restPool = ALL_SCROLLYS.filter((s) => !s.featured);
    const inTrending = new Set(trendingRow.map((s) => s.id));
    const ordered = [...featuredPool, ...restPool].filter(
      (s) => !inTrending.has(s.id) && s.id !== featured.id,
    );
    return ordered.slice(0, 4);
  }, [featured.id, trendingRow]);

  // Row 3 — Kategorie-Row (Wissenschaft). Featured-first, bis zu 4 Items.
  // **Kein Dedup** gegen Row 1+2: eine Kategorie-Spotlight-Row soll ihr
  // Thema vollständig zeigen, auch wenn einzelne Cards bereits früher
  // im Feed gelaufen sind (so auch Quelle `CategorySpotlight`, HTML:1379).
  // Hero wird weiterhin ausgeschlossen, damit das Hauptbild nicht doppelt
  // klein darunter erscheint.
  const spotlightRow = useMemo(() => {
    const inCategory = ALL_SCROLLYS.filter(
      (s) =>
        s.categoryId === SPOTLIGHT_CATEGORY_ID && s.id !== featured.id,
    );
    const featuredFirst = [
      ...inCategory.filter((s) => s.featured),
      ...inCategory.filter((s) => !s.featured),
    ];
    return featuredFirst.slice(0, 4);
  }, [featured.id]);

  if (isLoading) {
    return <FeedSkeleton />;
  }

  return (
    <div style={{ background: v.bg, minHeight: "100%" }}>
      <CategoryChips />

      <div
        style={{
          padding: "32px 32px 80px",
          maxWidth: 1600,
          margin: "0 auto",
        }}
      >
        <LoggedOutHero />

        <div
          style={{
            display: "flex",
            justifyContent: "center",
            marginTop: 16,
            marginBottom: 16,
          }}
        >
          <FeaturedHero scrolly={featured} />
        </div>

        <SectionTitle
          title="Im Trend"
          subtitle="Die Scrollys, über die alle sprechen"
          more="Alle ansehen"
        />
        <CardGrid>
          {trendingRow.map((s) => (
            <ScrollyCard key={s.id} scrolly={s} />
          ))}
        </CardGrid>

        <SectionTitle
          title="Empfehlungen der Redaktion"
          more="Mehr davon"
        />
        <CardGrid>
          {editorsRow.map((s) => (
            <ScrollyCard key={s.id} scrolly={s} />
          ))}
        </CardGrid>

        <SectionTitle title={spotlightLabel()} more="Alle ansehen" />
        <CardGrid>
          {spotlightRow.map((s) => (
            <ScrollyCard key={s.id} scrolly={s} />
          ))}
        </CardGrid>
      </div>
    </div>
  );
}
